import { Leading } from '@sandtech-hub-monorepo/ui-kit-site';
import programBanner from '../../assets/banners/program-banner.webp';
import { Button, Card } from '@sandtech-hub-monorepo/ui-kit';
import { CheckIcon, CirclePlayIcon, Undo2Icon } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { Loading } from '@sandtech-hub-monorepo/ui-kit-site';
import {
  getDescriptionFromAbbreviation,
  getIconFromAbbreviation,
} from '@sandtech-hub-monorepo/ui-kit-site';
import {
  ClassEnrollmentStatus,
  GetViewerAvailableProgramsDocument,
} from '@sandtech-hub-monorepo/ehub-client';
import { CourseCardType, ProgramCourseCard } from './ProgramCourseCard';
import { UniversityProgramCourseCard } from './UniversityProgramCourseCard';
import { CoursesGroupView } from '../../Learning/ProgramEnrollmentsView/components/CoursesGroupView';
import { formatDate } from '../../dateFormat';
import ReactPlayer from 'react-player/youtube';
import { ProgramEnrollmentModal } from './ProgramEnrollmentModal';
import { useScrollRestoration } from '@sandtech-hub-monorepo/ui-kit-site';
import { useQuery } from '@apollo/client';
import { enrollButtonState } from '@sandtech-hub-monorepo/ui-kit-site';
import { useGetMaybeViewer } from '@sandtech-hub-monorepo/ui-auth-kit';

export const ProgramPage = ({
  learningPageLink,
  notFoundPageLink,
  programsPageLink,
}: {
  learningPageLink: string;
  notFoundPageLink: string;
  programsPageLink: string;
}) => {
  useScrollRestoration();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const { programId } = useParams();
  const { viewer } = useGetMaybeViewer();
  const { data, loading } = useQuery(GetViewerAvailableProgramsDocument, {
    fetchPolicy: 'cache-first',
  });

  const program = useMemo(
    () =>
      data?.viewer?.availablePrograms?.find(
        ({ abbreviation }) => abbreviation === programId
      ),
    [data?.viewer?.availablePrograms, programId]
  );

  const courses = useMemo(() => {
    return (
      program?.potentialProgramJourney?.potentialCourseEnrollments || []
    ).map(
      ({
        existingEnrollment,
        firstAvailableClass,
        courseDetails,
        courseGroupKey,
        credits,
      }) => {
        const course = courseDetails;
        const courseAbbreviation = course?.abbreviation || '';
        const courseName = course?.name;
        const courseDescription =
          course?.description ||
          getDescriptionFromAbbreviation(courseAbbreviation);
        const courseStartDate =
          existingEnrollment?.class.startDate || firstAvailableClass?.startDate;
        const courseEndDate =
          existingEnrollment?.class.endDate || firstAvailableClass?.endDate;

        let status = CourseCardType.TBA;
        if (existingEnrollment?.status === ClassEnrollmentStatus.Ongoing) {
          status = CourseCardType.ONGOING;
        } else if (
          existingEnrollment?.status === ClassEnrollmentStatus.Graduated
        ) {
          status = CourseCardType.COMPLETED;
        } else if (firstAvailableClass?.startDate) {
          status = CourseCardType.NEXT_CLASS;
        }

        return {
          id: course!.id,
          icon: getIconFromAbbreviation(courseAbbreviation, 'w-8 h-auto'),
          abbreviation: courseAbbreviation,
          name: courseName ?? '',
          description: courseDescription,
          status,
          startDate: courseStartDate ? new Date(courseStartDate) : undefined,
          endDate: courseEndDate ? new Date(courseEndDate) : undefined,
          courseGroupKey: courseGroupKey ?? null,
          credits: credits ?? 0,
        };
      }
    );
  }, [program]);

  const groupedCourseEntries = useMemo(() => {
    const groups: Record<string, typeof courses> = {};
    courses.forEach((course) => {
      const key = course.courseGroupKey || '';
      if (!groups[key]) groups[key] = [];
      groups[key].push(course);
    });
    return Object.entries(groups).map(([key, groupCourses]) => ({
      key,
      courses: groupCourses,
      totalCredits: groupCourses.reduce((sum, c) => sum + c.credits, 0),
    }));
  }, [courses]);

  if (!program) {
    if (!loading) {
      navigate(notFoundPageLink);
    }
    return <Loading />;
  }

  const { isEnrolled, isGraduated, canEnroll, isUniversityProgram } = program;
  const buttonState = enrollButtonState(
    isEnrolled,
    isGraduated,
    canEnroll,
    isUniversityProgram
  );

  return (
    <>
      <Leading
        pageName={program.name}
        title={program.marketing.title || ''}
        subtitle={program.marketing.subtitle || ''}
        img={programBanner}
      />
      <div className="sm:hidden block">
        <FeaturesCard
          video={program.marketing.video}
          highlights={program.marketing.highlights || []}
          thumbnail={program.marketing.thumbnail || ''}
        />
      </div>
      <div className="bg-banner p-6">
        <div className="flex max-w-screen-2xl mx-auto sm:flex-row sm:text-left flex-col text-center gap-1 sm:gap-0">
          <div className="px-6">
            <div className="text-banner-heading font-semibold">Duration</div>
            <div className="text-banner-subheading">
              {program.marketing.programDuration}
            </div>
          </div>
          <div className="px-6 sm:border-l border-accent-border">
            <div className="text-banner-heading font-semibold">Commitment</div>
            <div className="text-banner-subheading">
              {program.marketing.timeCommitment}
            </div>
          </div>
          <div className="px-6 sm:border-l border-accent-border">
            <div className="text-banner-heading font-semibold">Start Date</div>
            <div className="text-banner-subheading">
              {program.potentialProgramJourney?.startDate &&
                formatDate(program.potentialProgramJourney?.startDate)}
              {!program.potentialProgramJourney?.startDate && (
                <div className="flex-row">
                  <div className="text-warning">{CourseCardType.ONGOING}</div>
                </div>
              )}
            </div>
          </div>
          <div className="px-6 sm:border-l border-accent-border">
            <div className="text-banner-heading font-semibold">
              {program.requiresTuitionQuestionnaire
                ? 'Tuition Cost'
                : 'Access Fee'}
            </div>
            <div className="text-banner-subheading">
              {program.marketing.accessFee}
            </div>
          </div>
        </div>
      </div>
      <div className="flex max-w-screen-2xl mx-auto sm:px-6 px-2 lg:flex-row flex-col">
        <div className="py-6 sm:py-12 flex-1">
          <h1 className="text-2xl font-semibold mb-6 sm:text-4xl text-heading">
            {program.marketing.sectionTitle || ''}
          </h1>
          <div className="flex flex-col gap-2 sm:gap-4 sm:text-xl text-md">
            {program.marketing.sectionSummary?.split('\n').map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
          <h1 className="sm:text-3xl text-xl font-semibold mt-8 mb-4 text-heading">
            Program structure
          </h1>
          {isUniversityProgram ? (
            groupedCourseEntries.length === 1 ? (
              groupedCourseEntries[0].courses.map((course) => (
                <UniversityProgramCourseCard key={course.id} {...course} />
              ))
            ) : (
              <div className="border border-primary rounded-lg">
                {groupedCourseEntries.map(({ key, courses: groupCourses, totalCredits }, index) => (
                  <CoursesGroupView
                    key={key}
                    name={key}
                    totalCredits={totalCredits}
                    initialExpanded={index === 0}
                    isLastElement={index === groupedCourseEntries.length - 1}
                  >
                    {groupCourses.map((course) => (
                      <UniversityProgramCourseCard
                        key={course.id}
                        {...course}
                      />
                    ))}
                  </CoursesGroupView>
                ))}
              </div>
            )
          ) : (
            courses.map((course) => (
              <ProgramCourseCard key={course.id} {...course} />
            ))
          )}
        </div>
        <div className="flex-1">
          <Card className="sm:sticky fixed sm:top-10 max-h-[calc(100vh-40px)] overflow-auto bottom-0 left-0 px-4 sm:px-0 sm:rounded-lg border-none rounded-none w-full sm:w-3/4 mb-0 sm:mb-20 lg:mb-0 lg:-mt-40 z-10 mx-auto pb-3 sm:pb-6 sm:pt-0 pt-3 shadow-sm">
            <div className="sm:block hidden">
              <FeaturesCard
                video={program.marketing.video}
                highlights={program.marketing.highlights || []}
                thumbnail={program.marketing.thumbnail || ''}
              />
            </div>
            <Button
              disabled={buttonState.disabled}
              onClick={() => setConfirmOpen(true)}
              variant="enhanced"
              size="lg"
              className="rounded-full sm:w-3/4 w-full block mx-auto"
            >
              {buttonState.ctaText}
            </Button>
            <Link
              className="justify-center text-sm flex items-center text-center mt-3 text-primary hover:underline"
              to={programsPageLink}
            >
              Back to All Programs
              <Undo2Icon className="h-4" />
            </Link>
          </Card>
        </div>
      </div>
      <ProgramEnrollmentModal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        program={program}
        learningPageLink={learningPageLink}
        viewer={viewer}
      />
    </>
  );
};

const FeaturesCard = ({
  highlights,
  video,
  thumbnail,
}: {
  highlights: string[];
  video?: string | null;
  thumbnail: string;
}) => {
  const [videoStarted, setVideoStarted] = useState(false);

  return (
    <>
      <div className="relative w-full">
        <img src={thumbnail} className="w-full" alt="thumbnail" />
        {video && (
          <div
            onClick={() => setVideoStarted(true)}
            className="h-full w-full absolute z-10 bg-background/10 left-0 top-0 text-background/80 hover:bg-background/20 hover:text-background flex justify-center items-center cursor-pointer"
          >
            <CirclePlayIcon height={80} width={80} />
          </div>
        )}
        {videoStarted && (
          <ReactPlayer
            className="absolute top-0 left-0 z-20"
            controls
            url={video || ''}
            width="100%"
            height="100%"
            playing={true}
          />
        )}
      </div>
      <div className="sm:p-8 p-4 text-lg text-background sm:text-foreground relative">
        <img
          src={programBanner}
          className="sm:hidden block w-full h-full object-fill top-0 left-0 absolute z-0"
        />
        <div className="z-10 relative sm:p-6">
          <div className="font-semibold text-xl">
            Program Learning Outcomes:
          </div>
          {highlights.map((key, i) => (
            <div key={key} className="flex gap-4 mt-4">
              <CheckIcon
                width={20}
                height={20}
                className="min-w-5 text-lg block text-primary mt-1"
                strokeWidth={3}
              />
              {key}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
